import React from "react";

/**
 * Progress Loader Component
 * Perfect for multi-step operations, file uploads, and progress tracking
 */
const ProgressLoader = ({
  progress = 0, // 0-100
  total = 100,
  current = 0,
  showPercentage = true,
  showCurrent = false,
  size = "default", // "small", "default", "large"
  variant = "default", // "default", "success", "warning", "danger"
  animated = true,
  className = "",
  label = "",
  description = ""
}) => {
  // Calculate progress percentage
  const progressPercentage = total > 0 ? Math.min((current / total) * 100, 100) : progress;
  
  // Size configurations
  const sizeConfig = {
    small: {
      height: "h-1",
      text: "text-xs",
      label: "text-sm"
    },
    default: {
      height: "h-2",
      text: "text-sm",
      label: "text-base"
    },
    large: {
      height: "h-3",
      text: "text-base",
      label: "text-lg"
    }
  };

  const config = sizeConfig[size];

  // Variant configurations
  const variantConfig = {
    default: {
      bg: "bg-emerald-600",
      text: "text-emerald-600",
      border: "border-emerald-200"
    },
    success: {
      bg: "bg-green-600",
      text: "text-green-600",
      border: "border-green-200"
    },
    warning: {
      bg: "bg-yellow-600",
      text: "text-yellow-600",
      border: "border-yellow-200"
    },
    danger: {
      bg: "bg-red-600",
      text: "text-red-600",
      border: "border-red-200"
    }
  };

  const variantClass = variantConfig[variant];

  // Animation class
  const animationClass = animated ? "transition-all duration-300 ease-out" : "";

  return (
    <div className={`w-full ${className}`}>
      {/* Label and Description */}
      {(label || description) && (
        <div className="mb-2">
          {label && (
            <div className={`font-medium text-slate-900 ${config.label}`}>
              {label}
            </div>
          )}
          {description && (
            <div className={`text-slate-600 ${config.text} mt-1`}>
              {description}
            </div>
          )}
        </div>
      )}

      {/* Progress Bar Container */}
      <div className={`relative ${config.height} bg-slate-200 rounded-full overflow-hidden border ${variantClass.border}`}>
        {/* Progress Fill */}
        <div
          className={`${config.height} ${variantClass.bg} ${animationClass} rounded-full`}
          style={{ width: `${progressPercentage}%` }}
        ></div>
        
        {/* Animated Stripes (optional) */}
        {animated && progressPercentage > 0 && progressPercentage < 100 && (
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
        )}
      </div>

      {/* Progress Information */}
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center space-x-2">
          {showCurrent && (
            <span className={`text-slate-600 ${config.text}`}>
              {current} / {total}
            </span>
          )}
        </div>
        
        {showPercentage && (
          <span className={`font-medium ${variantClass.text} ${config.text}`}>
            {Math.round(progressPercentage)}%
          </span>
        )}
      </div>
    </div>
  );
};

export default ProgressLoader;
